import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link2, Lock, MapPin, X } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { Input } from "../components/ui/input";

const LAUNCH_DATE = new Date("2026-11-01T00:00:00-04:00");

const POLAROIDS = [
  {
    src: "https://images.unsplash.com/photo-1717646472043-8584872755ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    caption: "Farmers market artisans",
    rotate: "-rotate-3",
  },
  {
    src: "https://images.unsplash.com/photo-1590732261664-0ff7e1aef21a?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    caption: "Local artists & murals",
    rotate: "rotate-2",
  },
  {
    src: "https://images.unsplash.com/photo-1510972527921-ce03766a1cf1?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    caption: "Neighborhood coffee shops",
    rotate: "-rotate-2",
  },
  {
    src: "https://images.pexels.com/photos/5990283/pexels-photo-5990283.jpeg?auto=compress&cs=tinysrgb&w=600",
    caption: "Coastal promenade life",
    rotate: "rotate-3",
  },
];

function useCountdown() {
  const calc = () => {
    const diff = Math.max(0, LAUNCH_DATE.getTime() - Date.now());
    const s = Math.floor(diff / 1000);
    return {
      weeks: Math.floor(s / 604800),
      days: Math.floor((s % 604800) / 86400),
      hours: Math.floor((s % 86400) / 3600),
      seconds: s % 60,
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
  }, []);
  return t;
}

export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const [gatePw, setGatePw] = useState("");
  const [gateError, setGateError] = useState("");
  const navigate = useNavigate();
  const t = useCountdown();

  const subscribe = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/subscribe", { email });
      setDone(true);
      toast.success(r.data.already ? "You're already on the list!" : "You're in! Early access key reserved.");
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setLoading(false);
    }
  };

  const unlock = async (e) => {
    e.preventDefault();
    setGateError("");
    try {
      await api.post("/access-gate", { password: gatePw });
      localStorage.setItem("bl_access", "1");
      navigate("/login");
    } catch (e2) {
      setGateError(errMsg(e2));
    }
  };

  const cells = [
    { v: t.weeks, l: "Weeks" },
    { v: t.days, l: "Days" },
    { v: t.hours, l: "Hours" },
    { v: t.seconds, l: "Seconds" },
  ];

  return (
    <div data-testid="coming-soon-page" className="min-h-screen bg-[#1B2A4A] text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "28px 28px" }} />

      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#F5961B] flex items-center justify-center">
            <Link2 size={18} className="text-white" />
          </div>
          <div className="font-bold text-lg tracking-wide" data-testid="coming-soon-logo">BROWARD <span className="text-[#5FA3E8]">LOCAL</span></div>
        </div>
        <button
          data-testid="team-access-button"
          onClick={() => setGateOpen(true)}
          className="flex items-center gap-2 text-sm font-medium text-white/60 hover:text-white transition-colors border border-white/15 hover:border-white/40 rounded-full px-4 py-2"
        >
          <Lock size={14} /> Team Access
        </button>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-8 md:pt-14 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <div>
          <div className="inline-flex items-center gap-2 bg-[#F5961B]/15 border border-[#F5961B]/40 text-[#F5961B] text-xs font-bold uppercase tracking-widest rounded-full px-4 py-1.5 mb-6">
            <MapPin size={12} /> Broward County, Florida
          </div>
          <h1 data-testid="coming-soon-headline" className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] tracking-tight">
            Ready to explore?<br />
            <span className="text-[#5FA3E8]">Broward County's 1st</span><br />
            Local <span className="text-[#F5961B]">Verified</span> Marketplace.
          </h1>
          <p data-testid="coming-soon-subhead" className="mt-6 text-lg text-white/60 max-w-md leading-relaxed">
            Connecting you to 100+ unique, verified local vendors. Get exclusive early access.
          </p>

          {done ? (
            <div data-testid="subscribe-success" className="mt-8 bg-[#2E7BD6]/15 border border-[#2E7BD6]/50 rounded-2xl px-6 py-5 max-w-md">
              <div className="font-semibold text-lg">You're on the list.</div>
              <div className="text-white/60 text-sm mt-1">Your early access key is reserved — we'll email you the moment we open the gates.</div>
            </div>
          ) : (
            <form onSubmit={subscribe} className="mt-8 flex max-w-md rounded-2xl overflow-hidden border border-white/15 bg-white/5 backdrop-blur-sm focus-within:border-[#F5961B]/60 transition-colors">
              <input
                data-testid="subscribe-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email to unlock early access"
                className="flex-1 bg-transparent px-5 py-4 text-sm text-white placeholder:text-white/40 outline-none min-w-0"
              />
              <button
                data-testid="subscribe-submit-button"
                type="submit"
                disabled={loading}
                className="bg-[#F5961B] hover:bg-[#dd8512] transition-colors text-white font-bold text-sm px-6 tracking-wide disabled:opacity-60"
              >
                {loading ? "..." : "GET MY KEY"}
              </button>
            </form>
          )}

          <div className="mt-10" data-testid="countdown-widget">
            <div className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-3">Launching in</div>
            <div className="flex gap-3">
              {cells.map((c) => (
                <div key={c.l} data-testid={`countdown-${c.l.toLowerCase()}`} className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-center min-w-[72px]">
                  <div className="text-2xl md:text-3xl font-bold tabular-nums">{String(c.v).padStart(2, "0")}</div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-white/40 mt-1">{c.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="relative hidden lg:block" data-testid="polaroid-grid">
          <div className="grid grid-cols-2 gap-8 max-w-lg mx-auto">
            {POLAROIDS.map((p, i) => (
              <div
                key={p.caption}
                className={`bg-white rounded-lg p-3 pb-10 shadow-2xl shadow-black/40 relative transform ${p.rotate} hover:rotate-0 hover:scale-[1.03] transition-transform duration-300 ${i % 2 === 1 ? "mt-10" : ""}`}
                data-testid={`polaroid-${i}`}
              >
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-16 h-5 bg-[#F5961B]/70 rotate-[-4deg] rounded-sm" />
                <img src={p.src} alt={p.caption} className="w-full aspect-[4/5] object-cover rounded" loading="lazy" />
                <div className="absolute bottom-3 left-0 right-0 text-center text-[#1B2A4A] text-xs font-semibold">{p.caption}</div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <footer className="relative z-10 text-center pb-8 text-white/30 text-xs tracking-wide">
        Fort Lauderdale · Hollywood · Coral Springs · Pembroke Pines · Pompano Beach &amp; every Broward neighborhood
      </footer>

      {gateOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" data-testid="access-gate-modal">
          <div className="bg-white text-[#0F172A] rounded-2xl p-8 w-full max-w-sm relative">
            <button data-testid="access-gate-close" onClick={() => { setGateOpen(false); setGateError(""); }} className="absolute top-4 right-4 text-[#94A3B8] hover:text-[#0F172A]">
              <X size={18} />
            </button>
            <div className="w-11 h-11 rounded-xl bg-[#1B2A4A] flex items-center justify-center mb-4">
              <Lock size={18} className="text-[#F5961B]" />
            </div>
            <h2 className="text-xl font-bold text-[#1B2A4A]">Team Access</h2>
            <p className="text-sm text-[#475569] mt-1 mb-5">Enter the access password to preview the site.</p>
            <form onSubmit={unlock} className="space-y-3">
              <Input
                data-testid="access-password-input"
                type="password"
                required
                value={gatePw}
                onChange={(e) => setGatePw(e.target.value)}
                placeholder="Access password"
                autoFocus
              />
              {gateError && <div data-testid="access-gate-error" className="text-sm text-red-600">{gateError}</div>}
              <button data-testid="access-gate-submit" type="submit" className="w-full bg-[#1B2A4A] hover:bg-[#141f38] transition-colors text-white font-semibold text-sm rounded-lg py-3">
                Unlock
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
